/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // `relative tap-target`: the size scale below is under the 44px touch
  // minimum on default/sm/icon, so an invisible ::after extends the touchable
  // region to 44x44 on coarse pointers only. No layout or pixel changes -
  // see the .tap-target block in index.css for the full reasoning.
  "relative tap-target inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[background-color,transform,scale,box-shadow] duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:scale-[1.02] active:scale-[0.98]",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-primary/30 hover:scale-[1.02] active:scale-[0.98]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-[1.02] active:scale-[0.98]",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] active:scale-[0.98]",
        // after:hidden - this variant renders as inline text, usually inside a
        // sentence. A 44px block hit region would swallow taps on the words
        // either side of it, which is worse than a small target.
        // text-accent, not text-primary: --primary is 3.68:1 against the page
        // ground and fails AA as text. It stays correct as a BACKGROUND for the
        // filled variants above, where white sits on it at 5.12:1.
        link: "text-accent underline-offset-4 hover:underline after:hidden",
        // design-rules §1: weight + color + size in that order, not all three
        // on one element. The fill plus the text-lg step already make this the
        // loudest control on the page, so no font-semibold on top.
        hero: "bg-primary text-primary-foreground hover:bg-primary/90 text-lg hover:scale-[1.02] active:scale-[0.98]",
        // Pro CTAs get the gold identity (--pro) instead of the generic green
        // primary. Before this existed, Pro upsells were spread across
        // default/hero/outline+className and 5 of 8 rendered green, so the
        // crown icon was the only thing marking them as Pro. `pro`/`proHero`
        // mirror `default`/`hero`; `proSubtle` is the tinted secondary for
        // in-app nudges that must not outrank the surface's own primary.
        // focus-visible:ring-pro overrides the base ring-ring: the app's green
        // --ring on a gold button reads as a clash. tailwind-merge resolves the
        // pair (same ring-color family, variant wins), so the base class does
        // not need removing. Both rings clear WCAG 2.1 AA on this background.
        pro: "bg-pro text-pro-foreground hover:bg-pro/90 focus-visible:ring-pro hover:scale-[1.02] active:scale-[0.98]",
        proHero:
          "bg-pro text-pro-foreground hover:bg-pro/90 focus-visible:ring-pro text-lg hover:scale-[1.02] active:scale-[0.98]",
        proSubtle:
          "bg-pro/10 text-foreground border border-pro/30 hover:bg-pro/20 hover:border-pro/50 focus-visible:ring-pro hover:scale-[1.02] active:scale-[0.98]",
        glass: "glass-card hover:bg-card/60 text-foreground border border-border/50 md:backdrop-blur-lg hover:scale-[1.02] active:scale-[0.98]",
        "outline-solid": "border border-border/50 bg-background/50 hover:bg-accent hover:text-accent-foreground hover:scale-[1.02] active:scale-[0.98]",
        // For a control sitting ON a gradient or artwork, where the theme's
        // surface tokens do not apply because the background is not a theme
        // surface. Call sites were hardcoding white/black to achieve this,
        // which §3 allows only with an exception comment and which nobody
        // could then restyle centrally. The colours are locked to the
        // gradient, not the theme, which is exactly the §3 exception.
        onGradient:
          "bg-white/95 text-neutral-900 hover:bg-white focus-visible:ring-white/70 hover:scale-[1.02] active:scale-[0.98]",
        // A destructive action that must not outshout the page's primary —
        // "Cancel subscription" beside "Manage subscription". The filled
        // destructive is too loud for a secondary position, which is why call
        // sites reached for className overrides instead.
        destructiveOutline:
          "border border-destructive/40 bg-transparent text-destructive hover:bg-destructive/10 hover:border-destructive/60 hover:scale-[1.02] active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
